import {
  DEFAULT_PREVIEW_PAGE_COUNTS,
  DEFAULT_QUANTITY_BREAK_TIERS,
  ENUM_PROPERTY_VALUES,
  ItemEnumProperty,
  ItemNumericProperty,
  PropertyCharacteristic,
  RateConfig,
  RateConfigDocument
} from "./RateConfig";

/**
 * Lightweight item shape consumed by the rate evaluator. The preview matrix
 * generates concrete values by enumerating combinations of the dimensions
 * that the scope's factors actually condition on.
 */
export interface PreviewItem {
  pages: number;
  width: number;
  height: number;
  weight: number;
  thickness: number;
  caseQuantity: number;
  interiorType: string;
  bindingType: string;
  laminateType: string;
  paperType: string;
  /**
   * Stringified `PackageType.ID`. Only meaningful for packing-rate
   * evaluation (where the engine is handed a package-aware item — see
   * shared/PackingRateConfig.md). For production-rate items this is
   * empty; a production factor never groups on `packageType` so the
   * empty value is invisible to the engine.
   */
  packageType: string;
  /**
   * Stringified `Facility.ID` (a.k.a. PrintNode id). An order-level
   * constant — every item in an order is produced/packed at one
   * facility — so the preview matrix and the per-item evaluator both
   * accept it as an item-level field. Empty when no facility is in
   * scope; only matches wildcard cells in that case. The order-level
   * evaluator path (`evaluateOrder`) reads `OrderRateInput.facility`
   * in preference to this item-level value when both are set.
   */
  facility?: string;
}

/** Reasonable defaults for the non-enumerated dimensions. */
export const PREVIEW_ITEM_DEFAULTS: Pick<PreviewItem, 'width' | 'height' | 'weight' | 'thickness' | 'caseQuantity'> = {
  width: 432,        // 6 in
  height: 648,       // 9 in
  weight: 0.75,
  thickness: 0.5,
  caseQuantity: 1
};

export function getEnumValue(item: PreviewItem, prop: ItemEnumProperty): string {
  return (item as any)[prop] || '';
}

export function getNumericValue(item: PreviewItem, prop: ItemNumericProperty): number {
  const v = (item as any)[prop];
  return typeof v === 'number' ? v : 0;
}

/**
 * Resolve which propertyCharacteristics actually appear in any factor's
 * groups for this scope.
 */
export function relevantPropertyCharacteristics(config: Partial<RateConfig> | undefined): PropertyCharacteristic[] {
  if (!config || !config.factors) { return []; }
  const set: { [k in PropertyCharacteristic]?: boolean } = {};
  for (const f of config.factors) {
    for (const g of (f.groups || [])) {
      for (const c of (g.characteristics || [])) { set[c] = true; }
    }
  }
  return Object.keys(set) as PropertyCharacteristic[];
}

/**
 * Resolve which numeric properties actually vary across the factors of a
 * particular scope. The preview UI iterates over the document's
 * `_previewPageCounts` only when `pages` is in this set.
 */
export function relevantNumericProperties(config: Partial<RateConfig> | undefined): ItemNumericProperty[] {
  if (!config || !config.factors) { return []; }
  const set: { [k in ItemNumericProperty]?: boolean } = {};
  for (const f of config.factors) {
    if (f.property) { set[f.property] = true; }
  }
  return Object.keys(set) as ItemNumericProperty[];
}

/**
 * Generate the cartesian product of preview rows across the enum-valued
 * propertyCharacteristics that the scope's factors actually use.
 *
 * `quantityBreak` and `numberUp` are NOT enumerated here — they are
 * derived per row at evaluation time from the row's order quantity
 * (the preview iterates per quantity tier separately) and from
 * `width`/`height` respectively.
 */
export function buildPreviewItems(doc: RateConfigDocument | undefined, scope: string): PreviewItem[] {
  const config = (doc ? doc[scope] : undefined) as Partial<RateConfig> | undefined;
  if (!config) { return []; }

  const usedChars = relevantPropertyCharacteristics(config);
  const enumProps: ItemEnumProperty[] = (['interiorType', 'bindingType', 'laminateType', 'paperType', 'packageType', 'facility'] as ItemEnumProperty[])
    .filter(p => usedChars.indexOf(p) >= 0);

  const numericProps = relevantNumericProperties(config);
  const pageCounts: number[] = (numericProps.indexOf('pages') >= 0)
    ? ((doc && Array.isArray(doc._previewPageCounts) && doc._previewPageCounts.length > 0)
        ? doc._previewPageCounts
        : DEFAULT_PREVIEW_PAGE_COUNTS)
    : [200];

  const enumValueLists: { prop: ItemEnumProperty; values: string[] }[] = enumProps.map(p => ({
    prop: p,
    values: ENUM_PROPERTY_VALUES[p].length > 0 ? ENUM_PROPERTY_VALUES[p] : ['']
  }));

  let combos: { [k in ItemEnumProperty]?: string }[] = [{}];
  for (const e of enumValueLists) {
    const next: { [k in ItemEnumProperty]?: string }[] = [];
    for (const acc of combos) {
      for (const v of e.values) {
        next.push({...acc, [e.prop]: v});
      }
    }
    combos = next;
  }

  const items: PreviewItem[] = [];
  for (const pages of pageCounts) {
    for (const combo of combos) {
      items.push({
        ...PREVIEW_ITEM_DEFAULTS,
        pages,
        interiorType: combo.interiorType || 'GRAY',
        bindingType:  combo.bindingType  || 'PERFECT',
        laminateType: combo.laminateType || 'GLOSS',
        paperType:    combo.paperType    || '',
        packageType:  combo.packageType  || '',
        facility:     combo.facility     || ''
      });
    }
  }

  if (items.length === 0) {
    items.push({
      ...PREVIEW_ITEM_DEFAULTS,
      pages: 200,
      interiorType: 'GRAY',
      bindingType:  'PERFECT',
      laminateType: 'GLOSS',
      paperType:    '',
      packageType:  '',
      facility:     ''
    });
  }

  return items;
}

/**
 * Resolve the quantity tier list for a scope: the scope's
 * `defaultQuantityBreaks` if present, otherwise the global default.
 */
export function resolvePreviewQuantityTiers(config: Partial<RateConfig> | undefined): { minQty: number; maxQty: number }[] {
  if (config && config.defaultQuantityBreaks && config.defaultQuantityBreaks.length > 0) {
    return config.defaultQuantityBreaks;
  }
  return DEFAULT_QUANTITY_BREAK_TIERS;
}

